import React from 'react';
import { baseURL } from '../../api/baseURL';
import axios from 'axios';
import { connect } from 'react-redux';
import Loader from '../../Loader';
import Modal from '../../Modal';
import ExportButton from '../ExportButton';
import { MDBBtn } from 'mdb-react-ui-kit';
import AdminShell from '../layout/AdminShell';
import { isMainAdmin } from '../../utils/isMainAdmin';
import { countPillStyle, chipBarStyle, chipStyle, toolbarRowStyle, searchBoxWrapperStyle, thStyle } from '../layout/listUi';

type ReviewRow = {
    receipt_id: string;
    service_type: string;
    slot_index: number;
    stars: number;
    comment: string;
    rated_at: string;
    flagged: boolean;
    featured: boolean;
    reply: string | null;
    replied_at: string | null;
    photographer_id: string | null;
    photographer_name: string;
    city: string;
    customer_name: string;
};

type State = {
    requested: boolean,
    err: boolean,
    reviews: ReviewRow[],
    breakdown: { stars: number, count: number }[],
    total: number,
    starsFilter: string,
    photographerFilter: string,
    cityFilter: string,
    flaggedOnly: boolean,
    replyDraftKey: string | null,
    replyDraftText: string,
};

const STAR_CHIPS = [
    { key: '', label: 'All ratings' },
    { key: '5', label: '5 stars' },
    { key: '4', label: '4 stars' },
    { key: '3', label: '3 stars' },
    { key: '2', label: '2 stars' },
    { key: '1', label: '1 star' },
];

const rowKey = (r: Pick<ReviewRow, 'receipt_id' | 'service_type' | 'slot_index'>) => `${r.receipt_id}-${r.service_type}-${r.slot_index}`;

/** Customer -> photographer reviews. Distinct from Customer Ratings
 * (photographer -> customer, internal-only) — this is the customer-facing
 * review data (service_slots[].customer_rating). Phase 8 of the redesign
 * adds Feature (curate for Home's "Recent Reviews" widget) and Reply
 * (admin's public response, stored alongside the rating) on top of the
 * pre-existing flag/unflag moderation action. */
class ReviewsPage extends React.Component<{ history: any, token: string, role?: string }> {

    state: State = {
        requested: false,
        err: false,
        reviews: [],
        breakdown: [],
        total: 0,
        starsFilter: '',
        photographerFilter: '',
        cityFilter: '',
        flaggedOnly: false,
        replyDraftKey: null,
        replyDraftText: '',
    }

    componentDidMount(): void {
        if (!this.props.token) {
            window.location.pathname = "/";
        }
        else {
            this.getList();
        }
    }

    getList = () => {
        if (this.state.requested) return;
        this.setState({ err: false, requested: true });
        const params = new URLSearchParams();
        if (this.state.starsFilter) params.set('stars', this.state.starsFilter);
        if (this.state.photographerFilter) params.set('photographerId', this.state.photographerFilter);
        if (this.state.cityFilter) params.set('city', this.state.cityFilter);
        if (this.state.flaggedOnly) params.set('flagged', 'true');
        axios.get(`${baseURL}/reviews?${params.toString()}`,
            {
                headers: { Authorization: `Bearer ${this.props.token}` }
            })
            .then((response: any) => {
                if (response?.data?.message === "success") {
                    this.setState({
                        reviews: response?.data?.reviews || [],
                        breakdown: response?.data?.breakdown || [],
                        total: response?.data?.total || 0,
                        requested: false, err: false,
                    })
                }
                else {
                    this.setState({ err: true, requested: false });
                }
            })
            .catch(() => {
                this.setState({ err: true, requested: false });
            })
    }

    toggleModal = () => {
        if (this.state.err) {
            this.setState({ err: false });
        }
    }

    onFilterChange = (key: 'starsFilter' | 'photographerFilter' | 'cityFilter', value: string) => {
        this.setState({ [key]: value } as any, () => this.getList());
    }

    toggleFlaggedOnly = () => {
        this.setState({ flaggedOnly: !this.state.flaggedOnly }, () => this.getList());
    }

    patchReview = (review: ReviewRow, patch: Partial<ReviewRow>) => {
        this.setState({
            reviews: this.state.reviews.map(r => rowKey(r) === rowKey(review) ? { ...r, ...patch } : r),
        });
    }

    setFlag = (review: ReviewRow, flagged: boolean) => {
        axios.post(`${baseURL}/${flagged ? 'flag-review' : 'unflag-review'}`,
            { receipt_id: review.receipt_id, service_type: review.service_type, slot_index: review.slot_index },
            { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === "success") this.patchReview(review, { flagged });
            })
            .catch(() => {});
    }

    setFeatured = (review: ReviewRow, featured: boolean) => {
        axios.post(`${baseURL}/${featured ? 'feature-review' : 'unfeature-review'}`,
            { receipt_id: review.receipt_id, service_type: review.service_type, slot_index: review.slot_index },
            { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === "success") this.patchReview(review, { featured });
            })
            .catch(() => {});
    }

    startReply = (review: ReviewRow) => {
        this.setState({ replyDraftKey: rowKey(review), replyDraftText: review.reply || '' });
    }

    cancelReply = () => {
        this.setState({ replyDraftKey: null, replyDraftText: '' });
    }

    submitReply = (review: ReviewRow) => {
        const replyText = this.state.replyDraftText.trim();
        if (!replyText) return;
        axios.post(`${baseURL}/reply-review`,
            { receipt_id: review.receipt_id, service_type: review.service_type, slot_index: review.slot_index, reply: replyText },
            { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === "success") {
                    this.patchReview(review, { reply: replyText, replied_at: new Date().toISOString() });
                    this.setState({ replyDraftKey: null, replyDraftText: '' });
                }
            })
            .catch(() => {});
    }

    render() {
        const mainAdmin = isMainAdmin(this.props.role);
        const maxBreakdownCount = Math.max(1, ...this.state.breakdown.map(b => b.count));
        return (
            <AdminShell activePage="reviews" title="Reviews" subtitle="Customer ratings and public replies" role={this.props.role}>
                <Modal open={this.state.err} hideSec primaryText="Okay" title='Update' onConfirm={this.toggleModal} toggleOpen={this.toggleModal} content={<h6>Something went wrong, please try again later</h6>} />
                {this.state.requested ? <Loader /> : null}

                <div className="card" style={{ marginBottom: 16 }}>
                    <div className="card-body">
                        <span style={{ fontSize: 13, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.4 }}>Star Rating Breakdown</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                            {this.state.breakdown.slice().reverse().map(b => (
                                <div key={b.stars} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ width: 50, fontSize: 13 }}>{b.stars} star{b.stars > 1 ? 's' : ''}</span>
                                    <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 12 }}>
                                        <div style={{ width: `${(b.count / maxBreakdownCount) * 100}%`, background: '#FFB020', height: '100%', borderRadius: 4 }} />
                                    </div>
                                    <span style={{ width: 30, fontSize: 13, textAlign: 'right' }}>{b.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
                            <h4 className="card-title" style={{ marginBottom: 0 }}>
                                Reviews
                                <span style={countPillStyle}>{this.state.total}</span>
                            </h4>
                            <ExportButton endpoint="/export/reviews" label="Export" />
                        </div>

                        <div style={chipBarStyle}>
                            {STAR_CHIPS.map(chip => (
                                <div key={chip.key} style={chipStyle(this.state.starsFilter === chip.key)} onClick={() => this.onFilterChange('starsFilter', chip.key)}>
                                    {chip.label}
                                </div>
                            ))}
                        </div>

                        <div style={toolbarRowStyle}>
                            <div style={searchBoxWrapperStyle}>
                                <input
                                    style={{ border: 'none', background: 'transparent', padding: '9px 14px', width: '100%', color: 'inherit', fontSize: 13 }}
                                    placeholder="Filter by city"
                                    value={this.state.cityFilter}
                                    onChange={(e) => this.onFilterChange('cityFilter', e.target.value)}
                                />
                            </div>
                            <MDBBtn
                                size="sm"
                                color={this.state.flaggedOnly ? 'danger' : 'secondary'}
                                onClick={this.toggleFlaggedOnly}
                            >
                                {this.state.flaggedOnly ? 'Showing Flagged Only' : 'Show Flagged Only'}
                            </MDBBtn>
                        </div>

                        <div className="table-responsive">
                            {(this.state.reviews?.length) ? <table className="table" style={{ borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Customer</th>
                                        <th style={thStyle}>Stars</th>
                                        <th style={thStyle}>Comment</th>
                                        <th style={thStyle}>Photographer</th>
                                        <th style={thStyle}>Service</th>
                                        <th style={thStyle}>City</th>
                                        <th style={thStyle}>Order</th>
                                        <th style={thStyle}>Rated On</th>
                                        <th style={thStyle}>Admin Reply</th>
                                        {mainAdmin ? <th style={thStyle}>Actions</th> : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.reviews.map((item: ReviewRow) => {
                                        const key = rowKey(item);
                                        const isReplying = this.state.replyDraftKey === key;
                                        return (
                                            <tr key={key} style={item.flagged ? { background: 'rgba(255,99,99,0.07)' } : undefined}>
                                                <td>
                                                    {item.customer_name || '—'}
                                                    {item.featured ? <span style={{ ...countPillStyle, background: 'rgba(255,176,32,0.16)', color: '#FFB020', marginLeft: 8 }}>★ Featured</span> : null}
                                                </td>
                                                <td>
                                                    <span className={`badge ${item.stars <= 2 ? 'badge-outline-danger' : item.stars === 3 ? 'badge-outline-warning' : 'badge-outline-success'}`}>
                                                        {'★'.repeat(item.stars)}{'☆'.repeat(5 - item.stars)}
                                                    </span>
                                                </td>
                                                <td>{item.comment || '—'}</td>
                                                <td>{item.photographer_name || '—'}</td>
                                                <td>{item.service_type || '—'}</td>
                                                <td>{item.city || '—'}</td>
                                                <td>{item.receipt_id}</td>
                                                <td>{item.rated_at ? new Date(item.rated_at).toLocaleString() : '—'}</td>
                                                <td style={{ minWidth: 220 }}>
                                                    {isReplying ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                            <textarea
                                                                className="form-control"
                                                                rows={2}
                                                                style={{ fontSize: 12.5 }}
                                                                value={this.state.replyDraftText}
                                                                onChange={(e) => this.setState({ replyDraftText: e.target.value })}
                                                                placeholder="Write a public reply…"
                                                            />
                                                            <div style={{ display: 'flex', gap: 6 }}>
                                                                <MDBBtn size="sm" onClick={() => this.submitReply(item)} disabled={!this.state.replyDraftText.trim()}>Post</MDBBtn>
                                                                <MDBBtn size="sm" color="secondary" onClick={this.cancelReply}>Cancel</MDBBtn>
                                                            </div>
                                                        </div>
                                                    ) : item.reply ? (
                                                        <div style={{ fontSize: 12.5 }}>
                                                            <div style={{ opacity: 0.85 }}>{item.reply}</div>
                                                            <div style={{ opacity: 0.45, fontSize: 10.5, marginTop: 2 }}>
                                                                {item.replied_at ? new Date(item.replied_at).toLocaleString() : ''}
                                                            </div>
                                                            {mainAdmin ? <span style={{ color: '#0090e7', fontSize: 11.5, cursor: 'pointer' }} onClick={() => this.startReply(item)}>Edit reply</span> : null}
                                                        </div>
                                                    ) : mainAdmin ? (
                                                        <span style={{ color: '#0090e7', fontSize: 12.5, cursor: 'pointer' }} onClick={() => this.startReply(item)}>Reply</span>
                                                    ) : '—'}
                                                </td>
                                                {mainAdmin ? (
                                                    <td>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                            <MDBBtn size="sm" color={item.flagged ? 'secondary' : 'danger'} onClick={() => this.setFlag(item, !item.flagged)}>
                                                                {item.flagged ? 'Unflag' : 'Flag'}
                                                            </MDBBtn>
                                                            <MDBBtn size="sm" color={item.featured ? 'secondary' : 'warning'} onClick={() => this.setFeatured(item, !item.featured)}>
                                                                {item.featured ? 'Unfeature' : 'Feature'}
                                                            </MDBBtn>
                                                        </div>
                                                    </td>
                                                ) : null}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table> : <div style={{ display: 'flex', justifyContent: 'center', padding: 15, paddingTop: 30 }}>
                                <div className="badge badge-outline-secondary" style={{ fontSize: 16 }}>No Data</div>
                            </div>}
                        </div>
                    </div>
                </div>
            </AdminShell>
        );
    }
}

const mapDispatchToProps = (dispatch: Function) => {
    return {
        dispatch
    }
}

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        token: state.auth.token,
        role: state.auth.role,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ReviewsPage);
